import { darkTheme } from '../../lib/tokens';
import { kanalKurz, kanalName, tagKurz } from './bausteine';
import { langfassungText } from './langfassung';
import type { LangfassungsStand, PublicationLage, ReviewLage, ReviewTarget, Termin } from './types';

const t = darkTheme.colors;

/**
 * Freigabe- und Plattform-Stand im Kalender (CUR-2246, Spec §3.2 und §5).
 *
 * Die Zustände kommen fertig abgeleitet vom Backend (`social.lage.ts`). Hier
 * wird nur gezeichnet — wer hier eine Regel nachbaut, hat zwei Wahrheiten.
 *
 * **Kein `danger`.** 👎 ist eine Bitte von Christine, kein Defekt, und ein
 * verpasster Termin ist eine Verschiebung, kein Verzug (Hausregel des Hubs).
 */

const FREIGABE_STIL: Record<Exclude<ReviewLage['state'], 'OHNE_REVIEW'>, { bg: string; fg: string }> = {
  // `textSecondary` statt `textTertiary`: Letzteres läge auf `bgElevated` bei
  // rund 2,9 : 1 und damit unter AA.
  ENTWURF: { bg: t.bgElevated, fg: t.textSecondary },
  WARTET: { bg: t.infoBg, fg: t.info },
  FREIGEGEBEN: { bg: t.successBg, fg: t.success },
  AENDERUNG_ERBETEN: { bg: t.warningBg, fg: t.warning },
  FREIGABE_AUFGEHOBEN: { bg: t.warningBg, fg: t.warning },
  ZURUECKGEZOGEN: { bg: t.bgElevated, fg: t.textSecondary },
};

/** ISO-Zeitpunkt → `Mo 12.10.` in der Zeit des Browsers. */
function tagAusZeitpunkt(iso: string): string {
  const d = new Date(iso);
  const tag = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return tagKurz(tag);
}

/** Der Text des Badges — ohne Emoji, für Screenreader und Tests. */
export function freigabeText(review: ReviewLage): string | null {
  // Ein Urteil je Video (K4): Die Instagram-Fassung zeigt, woher es kommt.
  const ueber = review.geerbtVon ? ` · über ${kanalName(review.geerbtVon)}` : '';
  switch (review.state) {
    case 'OHNE_REVIEW':
      // Mit Grund ist es eine bewusste Ausnahme (K2), ohne Grund veröffentlichter Altbestand.
      return review.verzicht ? 'Ohne Christine' : null;
    case 'ENTWURF':
      return 'Entwurf';
    case 'WARTET':
      return (review.submittedAt ? `Wartet auf Christine · seit ${tagAusZeitpunkt(review.submittedAt)}` : 'Wartet auf Christine') + ueber;
    case 'FREIGEGEBEN':
      return `Freigegeben${ueber}`;
    case 'AENDERUNG_ERBETEN':
      return `Änderung erbeten${ueber}`;
    case 'FREIGABE_AUFGEHOBEN':
      return 'Freigabe aufgehoben · neu einreichen';
    case 'ZURUECKGEZOGEN':
      return 'Zurückgezogen · neu einreichen';
  }
}

const FREIGABE_ZEICHEN: Partial<Record<ReviewLage['state'], string>> = {
  FREIGEGEBEN: '👍',
  AENDERUNG_ERBETEN: '👎',
  FREIGABE_AUFGEHOBEN: '👍',
};

/**
 * „Ohne Christine" (K2, CUR-2291): neutral, nicht als Mangel — die Ausnahme ist entschieden.
 * Daneben der Stand der 60-s-Fassung als Hinweis (D25), keine Sperre.
 */
function OhneChristine({ verzicht, langfassung }: { verzicht: string; langfassung: LangfassungsStand | null }): JSX.Element {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
        style={{ backgroundColor: t.bgElevated, color: t.textSecondary }} title={verzicht}>
        Ohne Christine
      </span>
      <span className="sr-only">, Grund: {verzicht}</span>
      {langfassung && (
        <span className="inline-flex items-center gap-1 text-xs" title={langfassung.anmerkung ?? undefined}
          style={{ color: langfassung.state === 'FREIGEGEBEN' ? t.success : t.textSecondary }}>
          {langfassung.state === 'FREIGEGEBEN' && <span aria-hidden>👍</span>}
          <span>{langfassungText(langfassung)}</span>
        </span>
      )}
    </span>
  );
}

export function FreigabeBadge({ review }: { review: ReviewLage }): JSX.Element | null {
  if (review.state === 'OHNE_REVIEW' && review.verzicht) return <OhneChristine verzicht={review.verzicht} langfassung={review.langfassung} />;
  const text = freigabeText(review);
  if (review.state === 'OHNE_REVIEW' || !text) return null;
  const stil = FREIGABE_STIL[review.state];
  const zeichen = FREIGABE_ZEICHEN[review.state];
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {/* `gap` statt Leerzeichen: In einem Flex-Container verschluckt der
          Browser Leerzeichen zwischen den Elementen („👍Freigegeben"). */}
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
        style={{ backgroundColor: stil.bg, color: stil.fg }}>
        {zeichen && <span aria-hidden>{zeichen}</span>}
        <span>{text}</span>
        {review.state === 'FREIGEGEBEN' && review.comment && (
          <span aria-hidden title={review.comment}>💬</span>
        )}
      </span>
      {review.state === 'FREIGEGEBEN' && review.comment && (
        <span className="sr-only">, Christine hat angemerkt: {review.comment}</span>
      )}
      {review.state === 'AENDERUNG_ERBETEN' && review.comment && (
        // Kein Anführungszeichen drumherum: Christines Anmerkung beginnt oft selbst
        // mit einem Zitat („„heilt" …").
        <span className="text-xs" style={{ color: t.textSecondary }}>Christine: {review.comment}</span>
      )}
      {review.mailFehler && (
        <span className="text-xs font-semibold" style={{ color: t.warning }}>
          Mail an Christine nicht zugestellt
        </span>
      )}
    </span>
  );
}

const CHIP_TEXT: Record<PublicationLage['state'], string> = {
  WARTET: 'wartet',
  OFFEN: 'offen',
  LIVE: 'live',
  VERSCHOBEN: 'verschoben',
  VERWORFEN: 'verworfen',
};

const CHIP_ZEICHEN: Partial<Record<PublicationLage['state'], string>> = {
  OFFEN: '○',
  LIVE: '✓',
};

/**
 * Ein Chip je Plattform. Das Plattform-Kürzel steht immer dabei — auch mobil:
 * Ein Zeichen allein wäre wieder „Farbe allein" (Hub §5.11).
 *
 * Live mit Link ist ein echter Link (neuer Tab); alles andere ist Text. Der
 * Chip liegt **neben** dem Zeilen-Link, nie darin — verschachtelte Links sind
 * ungültig und für Screenreader unbedienbar.
 */
export function PlattformChip({
  channel,
  publication,
  onLinkEintragen,
}: {
  channel: ReviewTarget;
  publication: PublicationLage;
  onLinkEintragen?: () => void;
}): JSX.Element {
  const name = kanalName(channel);
  const kurz = kanalKurz(channel);
  const zeichen = CHIP_ZEICHEN[publication.state];
  const text = CHIP_TEXT[publication.state];
  const gedimmt = publication.state === 'WARTET' || publication.state === 'VERWORFEN' || publication.state === 'VERSCHOBEN';
  const farbe = publication.state === 'LIVE' ? t.success : gedimmt ? t.textSecondary : t.textPrimary;
  const inhalt = (
    <>
      <span className="font-semibold">{kurz}</span>
      {zeichen && <span aria-hidden>{zeichen}</span>}
      <span>{text}</span>
      {publication.state === 'LIVE' && publication.url && <span aria-hidden>↗</span>}
    </>
  );
  const klasse = 'inline-flex min-h-[32px] items-center gap-1 rounded-md border px-2 text-xs';
  const stil = { borderColor: t.borderSubtle, color: farbe, backgroundColor: t.bgElevated };

  if (publication.state === 'LIVE' && publication.url) {
    return (
      <a href={publication.url} target="_blank" rel="noreferrer" className={klasse} style={stil}
        aria-label={`${name}: live, Link öffnen`}>
        {inhalt}
      </a>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <span className={klasse} style={stil}
        aria-label={`${name}: ${text}${publication.blockReason ? ` — ${publication.blockReason}` : ''}`}
        title={publication.blockReason ?? undefined}>
        {inhalt}
      </span>
      {publication.state === 'OFFEN' && onLinkEintragen && (
        <button type="button" onClick={onLinkEintragen}
          className="min-h-[32px] rounded-md px-2 text-xs font-semibold"
          style={{ color: t.accentLight }}>
          Von Hand erledigt — Link eintragen
        </button>
      )}
    </span>
  );
}

const LAGE_GRUND: Record<ReviewLage['state'], string> = {
  OHNE_REVIEW: 'offen',
  ENTWURF: 'noch nicht eingereicht',
  WARTET: 'noch bei Christine',
  FREIGEGEBEN: 'freigegeben, offen',
  AENDERUNG_ERBETEN: 'Änderung erbeten',
  FREIGABE_AUFGEHOBEN: 'Freigabe aufgehoben',
  ZURUECKGEZOGEN: 'zurückgezogen',
};

/**
 * „Termin Mo 13.10. vorbei · noch bei Christine" statt „überfällig".
 *
 * Die Lage steht dabei, weil „wartet auf Christine" eine andere Aufgabe ist
 * als „freigegeben, aber nicht hochgeladen" — und kein Verzugston (Hub §5.0).
 */
export function terminHinweis(termin: Termin): string | null {
  if (!termin.overdue || !termin.date) return null;
  return `Termin ${tagKurz(termin.date)} vorbei · ${LAGE_GRUND[termin.review.state]}`;
}

/** Der linke Rand heißt „braucht dich" — nicht „Freigabe-Farbe" (Spec §5.1). */
export function brauchtDich(termin: Termin): boolean {
  return (
    termin.overdue ||
    termin.review.state === 'AENDERUNG_ERBETEN' ||
    termin.review.state === 'FREIGABE_AUFGEHOBEN' ||
    termin.review.mailFehler
  );
}

export const LAGE_FILTER = [
  { key: 'christine', label: 'Wartet auf Christine', passt: (x: Termin) => x.review.state === 'WARTET' },
  {
    key: 'aenderung',
    label: 'Änderung erbeten',
    passt: (x: Termin) => x.review.state === 'AENDERUNG_ERBETEN' || x.review.state === 'FREIGABE_AUFGEHOBEN',
  },
  // Eine Fassung ohne Christine (K2) ist offen, aber nie freigegeben — sie gehört nicht hierher.
  { key: 'offen', label: 'Freigegeben, offen', passt: (x: Termin) => x.publication.state === 'OFFEN' && !x.review.verzicht },
  { key: 'live', label: 'Live', passt: (x: Termin) => x.publication.state === 'LIVE' },
  { key: 'dich', label: 'Braucht dich', passt: brauchtDich },
] as const;

export type LageFilterKey = (typeof LAGE_FILTER)[number]['key'];

export function istLageFilter(v: string | null): v is LageFilterKey {
  return LAGE_FILTER.some((f) => f.key === v);
}
